/*
 * Serialisierung der Search-Params für TanStack Router.
 *
 * ═══ WARUM DAS HIER STEHT ════════════════════════════════════════════════
 *
 * Der Default-Serialisierer von TanStack Router taugt für diese App NICHT.
 * Er JSON-kodiert Arrays in EINEN Parameter:
 *
 *   { loc: ["Lager Nord", "Lager Süd"] }
 *     → ?loc=%5B%22Lager+Nord%22%2C%22Lager+S%C3%BCd%22%5D
 *       (also  loc=["Lager Nord","Lager Süd"])
 *
 * Der Bestand und der Go-Port erwarten den WIEDERHOLTEN Parameter:
 *
 *   → ?loc=Lager+Nord&loc=Lager+S%C3%BCd
 *
 * Jede so geschriebene URL wäre für beide unlesbar. Und weil das Altsystem
 * bis zum Cutover produktiv bleibt und Suchen als Links geteilt und als
 * Lesezeichen abgelegt werden, ist das kein Schönheitsfehler, sondern ein
 * Bruch: geteilte Links aus dem neuen System kämen im alten falsch an.
 *
 * Zweiter Grund, den Default nicht zu nehmen: er JSON-PARST auch die Werte.
 * `q=123` würde zur Zahl 123, `q=true` zum Boolean. Für ein Suchfeld ist das
 * schlicht falsch — URL-Parameter sind Zeichenketten. Hier bleiben sie es;
 * die Umwandlung in Zahlen und Flags gehört in `validateSearch` der Route,
 * wo der erwartete Typ bekannt ist.
 *
 * Gebaut wird über `URLSearchParams` — dieselbe Klasse, die `buildSearchParams`
 * im Altsystem benutzt. Damit ist die Kodierung nicht nur kompatibel, sondern
 * zeichengleich (Leerzeichen als `+`, `:` als `%3A`).
 *
 * Gegengeprüft gegen `testdata/golden/search-params.json`, Abschnitt
 * `build-search-params` — siehe search-params.test.ts.
 */

/**
 * Ein Wert, wie er in der URL stehen darf. `string[]` wird zum wiederholten
 * Parameter, `""` bleibt als bedeutungstragender Marker erhalten
 * (`loc=` heißt „alle Standorte", nicht „nichts").
 */
export type SearchWert = string | number | boolean | readonly (string | number)[] | undefined | null;

export type SearchObjekt = Record<string, SearchWert>;

/**
 * Objekt → Query-String.
 *
 * Reihenfolge ist die Einfüge-Reihenfolge des Objekts; es wird NICHT sortiert.
 * Sie ist im Golden-Master Teil des gepinnten Vertrags.
 *
 * `undefined` und `null` werden weggelassen, `""` NICHT — siehe Marker oben.
 * Ein leeres Array erzeugt keinen Parameter; wer „alle" meint, übergibt `""`.
 */
export function stringifySearch(search: SearchObjekt): string {
  const usp = new URLSearchParams();

  for (const [key, wert] of Object.entries(search)) {
    if (wert === undefined || wert === null) continue;

    if (Array.isArray(wert)) {
      // Wiederholter Parameter, in Array-Reihenfolge.
      for (const eintrag of wert) usp.append(key, String(eintrag));
      continue;
    }
    usp.append(key, String(wert));
  }

  const query = usp.toString();
  return query.length > 0 ? `?${query}` : "";
}

/**
 * Query-String → Objekt.
 *
 * Ein Schlüssel, der MEHRFACH vorkommt, wird zum Array; ein einzelnes
 * Vorkommen bleibt ein String. Damit ist der Roundtrip nicht typstabil
 * (`["A"]` → `?loc=A` → `"A"`) — das ist keine Schwäche, sondern die
 * Eigenschaft der Kodierung selbst: `?loc=A` trägt die Information nicht,
 * ob ein Array gemeint war. Die Vereinheitlichung gehört in `validateSearch`,
 * wo der erwartete Typ je Parameter bekannt ist.
 *
 * Alle Werte bleiben Zeichenketten. Keine JSON-Deutung — siehe oben.
 */
export function parseSearch(searchStr: string): Record<string, string | string[]> {
  const usp = new URLSearchParams(searchStr.startsWith("?") ? searchStr.slice(1) : searchStr);
  const ergebnis: Record<string, string | string[]> = {};

  for (const key of new Set(usp.keys())) {
    const werte = usp.getAll(key);
    // `werte` ist nie leer — `keys()` liefert nur vorhandene Schlüssel.
    ergebnis[key] = werte.length === 1 ? werte[0]! : werte;
  }
  return ergebnis;
}
